import apiV2 from "api-v2";
import { AxiosError, AxiosResponse } from "axios";
import { downloadFileNoPopup } from "helpers/linkHelper";
import { ApiResponse } from "types/common.types";
import { FileableFile } from "types/storage.types";
import { showToast } from "utils/helpers";
import { PatchAddendumRequest } from "./addendum.types";

export const updateAddendumAnswer = async (
	addendumId: string,
	data: PatchAddendumRequest
) => {
	try {
		const response = await apiV2.patch<ApiResponse<any>>(
			`/addendum/${addendumId}/update-answer`,
			data
		);
		return response.data;
	} catch (error) {
		console.log(error);
	}
};

const BUNDLE_POLL_INTERVAL_MS = 2000;
const BUNDLE_POLL_ATTEMPTS = 30;

const wait = (ms: number) =>
	new Promise((resolve) => {
		setTimeout(resolve, ms);
	});

/**
 * Fetches an addendum's "download all" bundle and hands it to the browser.
 *
 * The zip is built on the queue, so the endpoint answers 202 while that is in flight. We
 * poll until it is ready rather than making the user click again, giving up after roughly
 * a minute so a failed build cannot spin forever.
 */
export const downloadAllAddendumFiles = async (questionId: number | string) => {
	try {
		for (let attempt = 0; attempt < BUNDLE_POLL_ATTEMPTS; attempt++) {
			// eslint-disable-next-line no-await-in-loop
			const response = await apiV2.get<AxiosResponse<FileableFile>>(
				`addendum/${questionId}/download-files`
			);

			const file = response.data?.data;

			if (response.status === 200 && file) {
				// newWindow: false — the zip is fetched after polling, so the click gesture
				// is long gone and a target="_blank" anchor is treated as a pop-up and
				// blocked. A same-tab anchor download has no such restriction.
				downloadFileNoPopup(file.file_path, file.file_name, false);
				return true;
			}

			if (response.status !== 202) {
				return false;
			}

			// eslint-disable-next-line no-await-in-loop
			await wait(BUNDLE_POLL_INTERVAL_MS);
		}

		showToast(
			"Your zip file is still being prepared, please try again shortly.",
			"Error"
		);

		return false;
	} catch (e) {
		// apiV2's response interceptor already toasts for any error carrying a response
		// (400 nothing to bundle, 403 unauthorised). Toasting again here would stack two
		// messages for the same failure, so only cover the case the interceptor cannot
		// describe: no response at all (network/timeout).
		if (!(e as AxiosError)?.response) {
			showToast("Failed to download addendum files", "Error");
		}

		return false;
	}
};
